import { RefObject, useEffect, useState } from "react";

export { useChallengeQuery, useTeamChallengeTotals } from "../ChallengesTable/useChallengeQuery";
export type { ChallengeState } from "../ChallengesTable/useChallengeQuery";

// An image that never fires load/error (a stalled CDN) must not hold the
// unattended printer forever.
export const READY_TIMEOUT_MS = 20000;

const settled = (img: HTMLImageElement) =>
  img.complete
    ? Promise.resolve(img.naturalWidth > 0 || !img.src)
    : new Promise<boolean>((resolve) => {
        img.addEventListener("load", () => resolve(true), { once: true });
        img.addEventListener("error", () => resolve(false), { once: true });
      });

/**
 * The page's print-readiness for headless printing (postgame packet Phase 3):
 * once the data requests have settled (`pending` false), wait for every image
 * under `root` and the web fonts, then two frames so layout has committed.
 * Failures never block — they're reported alongside the flag instead.
 */
export function useReportReady(
  root: RefObject<HTMLElement>,
  pending: boolean,
  dataErrors: string[],
): { ready: boolean; errors: string[] } {
  const [state, setState] = useState<{ ready: boolean; errors: string[] }>({
    ready: false,
    errors: [],
  });
  const errorKey = dataErrors.join(",");

  useEffect(() => {
    setState({ ready: false, errors: [] });
    if (pending || !root.current) return;
    let cancelled = false;
    const images = Array.from(root.current.querySelectorAll("img"));
    const loaded = Promise.all(images.map(settled)).then((ok) =>
      ok.every(Boolean) ? [] : ["images"],
    );
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<string[]>((resolve) => {
      timer = setTimeout(() => resolve(["timeout"]), READY_TIMEOUT_MS);
    });
    Promise.race([loaded, timeout])
      .then(async (imageErrors) => {
        clearTimeout(timer);
        await (document as any).fonts?.ready;
        await new Promise((r) =>
          requestAnimationFrame(() => requestAnimationFrame(r)),
        );
        return imageErrors;
      })
      .then((imageErrors) => {
        if (cancelled) return;
        setState({
          ready: true,
          errors: [...(errorKey ? errorKey.split(",") : []), ...imageErrors],
        });
      });
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [root, pending, errorKey]);

  return state;
}
